/**
 * ConnectDataSourceWizard Component
 * Step-by-step wizard for connecting a new data source or selecting an existing one
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useDataSourceMutations,
  useDataSourceSchema,
  useScopedDataSources,
  useGoogleSheets,
  useExcelDrives,
  useExcelWorkbooks,
  useExcelWorksheets,
} from '../hooks';
import type { DataSourceProvider, OwnershipType } from '../models/DataSource';
import { isReusableSourceForFolder } from './ConnectDataSourceWizardVisibility';
import type { FieldMapping } from '../models/DiagramDataSourceConnection';
import { ArrowLeft, ChevronRight, CircleCheckBig, Loader2, Plus, RefreshCw, Search, Upload } from 'lucide-react';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Combobox } from '../../../components/ui/combobox';
import { Input } from '../../../components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';

interface ConnectDataSourceWizardProps {
  onComplete?: (sourceId: string, fieldMappings?: FieldMapping[]) => void;
  onCancel?: () => void;
  folderId?: string;
  allowedProviders?: readonly DataSourceProvider[];
  skipFieldMapping?: boolean;
  /** The folder importer owns the dialog chrome and provider selection. */
  embedded?: boolean;
  onBack?: () => void;
  profile?: {
    domainId?: string;
    requiredProps?: string[];
    recommendedProps?: string[];
    fieldAliases?: Record<string, string[]>;
  };
}

type WizardStep =
  | 'choose'
  | 'provider'
  | 'config'
  | 'bamboohr_config'
  | 'excel_select'
  | 'mapping'
  | 'schema'
  | 'done';
type OAuthFlowState = 'idle' | 'authorizing' | 'success' | 'error';

interface OAuthCallbackMessage {
  type: 'oauth_callback';
  success: boolean;
  error: string | null;
}

interface ProviderInfo {
  id: DataSourceProvider;
  name: string;
  description: string;
  iconPath?: string;
  fallbackIcon: string;
  available: boolean;
}

interface ComingSoonProviderInfo {
  id: 'hibob' | 'workday' | 'ukg';
  name: string;
  description: string;
  fallbackIcon: string;
}

const ORG_CHART_ONLY_PROVIDERS = new Set<DataSourceProvider>([
  'google_workspace',
  'azure_ad',
  'bamboohr',
]);

const RESTRICTED_ADMIN_PROVIDERS = new Set<DataSourceProvider>([
  'google_workspace',
  'azure_ad',
  'bamboohr',
]);

const PER_USER_PROVIDER_OWNERSHIP = new Set<DataSourceProvider>([
  'google_sheets',
  'excel_online',
]);

const CENTRALIZED_PROVIDER_OWNERSHIP = new Set<DataSourceProvider>([
  'google_workspace',
  'azure_ad',
  'bamboohr',
]);

function isRestrictedAdminProvider(
  provider: DataSourceProvider | null
): provider is 'google_workspace' | 'azure_ad' | 'bamboohr' {
  return !!provider && RESTRICTED_ADMIN_PROVIDERS.has(provider);
}

function isLikelyProviderPermissionError(message: string | null | undefined): boolean {
  if (!message) return false;
  return (
    /\b(400|401|403)\b/.test(message) ||
    /\b(http\s*400|http\s*401|http\s*403)\b/i.test(message) ||
    /\b(unauthorized|forbidden|permission|permissions|insufficient|admin consent|access denied|not authorized)\b/i.test(
      message
    )
  );
}

function isLikelyOAuthCredentialError(message: string | null | undefined): boolean {
  return Boolean(message && /(?:no valid access token|token expired|credentials? expired|reconnect)/i.test(message));
}

function isValidGoogleSpreadsheetReference(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;

  // Full Google Sheets URL (with document id)
  if (/^https:\/\/docs\.google\.com\/spreadsheets\/d\/[^/]+/i.test(trimmed)) {
    return true;
  }

  // Raw spreadsheet id
  if (/^[a-zA-Z0-9-_]{20,}$/.test(trimmed)) {
    return true;
  }

  return false;
}

const PROVIDERS: ProviderInfo[] = [
  {
    id: 'google_sheets',
    name: 'Google Sheets',
    description: 'Connect spreadsheets in Google Drive',
    iconPath: '/assets/app-icons/data-integration/sheets-import.svg',
    fallbackIcon: '📊',
    available: true,
  },
  {
    id: 'csv',
    name: 'CSV Upload',
    description: 'Upload a CSV file (one-time import)',
    iconPath: '/assets/app-icons/data-integration/csv-import.svg',
    fallbackIcon: '📄',
    available: true,
  },
  {
    id: 'excel_online',
    name: 'Excel Online',
    description: 'Connect to Microsoft Excel Workbooks',
    iconPath: '/assets/app-icons/data-integration/excel.png',
    fallbackIcon: '📗',
    available: true,
  },
  {
    id: 'azure_ad',
    name: 'Azure Active Directory',
    description: 'Connect to Microsoft Entra ID directory',
    iconPath: '/assets/app-icons/data-integration/entra.png',
    fallbackIcon: '🟦',
    available: true,
  },
  {
    id: 'bamboohr',
    name: 'BambooHR',
    description: 'Connect to BambooHR employee directory',
    iconPath: '/assets/app-icons/data-integration/bamboohr.png',
    fallbackIcon: '🎋',
    available: true,
  },
  {
    id: 'google_workspace',
    name: 'Google Workspace',
    description: 'Connect to Google Workspace',
    iconPath: '/assets/app-icons/data-integration/google-workspace.png',
    fallbackIcon: '🏢',
    available: true,
  },
  {
    id: 'airtable',
    name: 'Airtable',
    description: 'Connect to Airtable bases',
    fallbackIcon: '🗃️',
    available: false,
  },
];

const COMING_SOON_HRIS_PROVIDERS: ComingSoonProviderInfo[] = [
  {
    id: 'hibob',
    name: 'HiBob',
    description: 'HiBob employee directory and lifecycle sync',
    fallbackIcon: 'HB',
  },
  {
    id: 'workday',
    name: 'Workday',
    description: 'Workday HCM employee roster and hierarchy sync',
    fallbackIcon: 'WD',
  },
  {
    id: 'ukg',
    name: 'UKG',
    description: 'UKG workforce directory and org data sync',
    fallbackIcon: 'UKG',
  },
];

export const ConnectDataSourceWizard: React.FC<ConnectDataSourceWizardProps> = ({
  onComplete,
  onCancel,
  folderId,
  allowedProviders,
  skipFieldMapping = false,
  embedded = false,
  onBack,
  profile,
}) => {
  const { t } = useTranslation('diagram');
  // Fetch existing data sources
  const {
    data: existingSourcesData,
    allDataSources,
    isLoading: isLoadingExisting,
  } = useScopedDataSources(folderId ?? null);
  const existingSources = React.useMemo(
    () => existingSourcesData?.dataSources ?? [],
    [existingSourcesData?.dataSources],
  );

  // Start at 'choose' if there are existing sources, otherwise go straight to 'provider'
  const [step, setStep] = useState<WizardStep>(embedded ? 'provider' : 'choose');
  const [selectedProvider, setSelectedProvider] = useState<DataSourceProvider | null>(null);
  const [selectedExistingId, setSelectedExistingId] = useState<string | null>(null);
  const [existingSearch, setExistingSearch] = useState('');
  const [name, setName] = useState('');
  const [ownership, setOwnership] = useState<OwnershipType>('org');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [createdSourceId, setCreatedSourceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [oauthFlowState, setOauthFlowState] = useState<OAuthFlowState>('idle');
  const [oauthFlowMessage, setOauthFlowMessage] = useState<string | null>(null);
  const [selectedWorkbookId, setSelectedWorkbookId] = useState<string>('');
  const [selectedDriveId, setSelectedDriveId] = useState<string>('');
  const [selectedWorksheetId, setSelectedWorksheetId] = useState<string>('');
  const [googleSheetsRequested, setGoogleSheetsRequested] = useState(false);
  const [selectedSheetName, setSelectedSheetName] = useState<string>('');
  const [fieldMappingDraft, setFieldMappingDraft] = useState<Record<string, string | null>>({});
  const [isFieldMappingInitialized, setIsFieldMappingInitialized] = useState(false);
  const [showUnmappedFields, setShowUnmappedFields] = useState(false);
  const oauthWindowRef = useRef<Window | null>(null);
  const oauthSourceIdRef = useRef<string | null>(null);
  const lastRequestedSpreadsheetRef = useRef<string | null>(null);
  const csvFileInputRef = useRef<HTMLInputElement | null>(null);
  const embeddedInitializedRef = useRef(false);

  const { create, update, testConnection } = useDataSourceMutations();
  const { data: schemaData, isLoading: isLoadingSchema } = useDataSourceSchema(createdSourceId ?? undefined);
  const shouldLoadGoogleSheets =
    selectedProvider === 'google_sheets' &&
    step === 'config' &&
    oauthFlowState === 'success' &&
    !!createdSourceId &&
    googleSheetsRequested &&
    !!config.spreadsheetId;
  const { data: googleSheetsData, isLoading: isLoadingGoogleSheets, isFetching: isRefreshingGoogleSheets, error: googleSheetsError, refetch: refreshGoogleSheets } = useGoogleSheets(
    createdSourceId ?? undefined,
    config.spreadsheetId,
    shouldLoadGoogleSheets
  );
  const shouldLoadExcelResources =
    selectedProvider === 'excel_online' &&
    step === 'excel_select' &&
    oauthFlowState === 'success' &&
    !!createdSourceId;
  const { data: excelDrivesData, isLoading: isLoadingDrives, isFetching: isRefreshingDrives, error: drivesError, refetch: refreshDrives } = useExcelDrives(
    createdSourceId ?? undefined,
    shouldLoadExcelResources,
  );
  const { data: excelWorkbooksData, isLoading: isLoadingWorkbooks, isFetching: isRefreshingWorkbooks, error: workbooksError, refetch: refreshWorkbooks } = useExcelWorkbooks(
    createdSourceId ?? undefined,
    selectedDriveId || undefined,
    shouldLoadExcelResources
  );
  const { data: excelWorksheetsData, isLoading: isLoadingWorksheets, isFetching: isRefreshingWorksheets, error: worksheetsError, refetch: refreshWorksheets } = useExcelWorksheets(
    createdSourceId ?? undefined,
    selectedDriveId || undefined,
    selectedWorkbookId || undefined,
    shouldLoadExcelResources && !!selectedWorkbookId
  );
  const isOrgChartProfile = profile?.domainId === 'orgchart';
  const availableProviders = React.useMemo(
    () =>
      PROVIDERS.filter((provider) => {
        if (allowedProviders && !allowedProviders.includes(provider.id)) {
          return false;
        }
        if (ORG_CHART_ONLY_PROVIDERS.has(provider.id) && !isOrgChartProfile) {
          return false;
        }
        return true;
      }),
    [allowedProviders, isOrgChartProfile]
  );
  const translateProvider = useCallback(
    (provider: Pick<ProviderInfo, 'id' | 'name' | 'description'> | ComingSoonProviderInfo) => ({
      name: t(`assistant.dataSourceProvider.${provider.id}.name`, { defaultValue: provider.name }) as string,
      description: t(`assistant.dataSourceProvider.${provider.id}.description`, {
        defaultValue: provider.description,
      }) as string,
    }),
    [t],
  );
  const translateOauthPermission = useCallback(
    (provider: 'google_workspace' | 'azure_ad' | 'bamboohr') =>
      String(t(`assistant.oauthPermission.${provider}`)),
    [t],
  );
  const allowedProviderIds = React.useMemo(
    () => new Set(availableProviders.map((provider) => provider.id)),
    [availableProviders]
  );
  const supportsOAuth = (
    provider: DataSourceProvider | null
  ): provider is 'google_sheets' | 'google_workspace' | 'excel_online' | 'azure_ad' =>
    provider === 'google_sheets' ||
    (provider === 'google_workspace' && isOrgChartProfile) ||
    provider === 'excel_online' ||
    provider === 'azure_ad';
  const supportsFieldMappingStep = React.useCallback(
    (provider: DataSourceProvider | null): boolean =>
      provider === 'google_sheets' ||
      (provider === 'google_workspace' && isOrgChartProfile) ||
      provider === 'excel_online' ||
      provider === 'azure_ad' ||
      provider === 'bamboohr',
    [isOrgChartProfile],
  );
  const oauthConnectedByProvider = React.useMemo(() => {
    const map = new Map<DataSourceProvider, { id: string; connected: boolean }>();
    for (const source of allDataSources) {
      const isSourceOAuthProvider =
        source.provider === 'google_sheets' ||
        source.provider === 'excel_online' ||
        (source.provider === 'azure_ad' && isOrgChartProfile) ||
        (source.provider === 'google_workspace' && isOrgChartProfile);
      if (!isSourceOAuthProvider || source.credentialSourceId) continue;
      const current = map.get(source.provider);
      const isConnected = source.status === 'connected';
      if (!current || (isConnected && !current.connected)) {
        map.set(source.provider, { id: source.id, connected: isConnected });
      }
    }
    return map;
  }, [allDataSources, isOrgChartProfile]);
  const singleProvider = embedded && availableProviders.length === 1 ? availableProviders[0] : null;
  useEffect(() => {
    if (!singleProvider || isLoadingExisting || embeddedInitializedRef.current) return;
    embeddedInitializedRef.current = true;
    // Reuse an existing spreadsheet account without opening OAuth or creating an endpoint.
    const connection = oauthConnectedByProvider.get(singleProvider.id);
    if (!PER_USER_PROVIDER_OWNERSHIP.has(singleProvider.id) || !connection?.connected) return;
    setSelectedProvider(singleProvider.id);
    setOwnership('personal');
    setCreatedSourceId(connection.id);
    oauthSourceIdRef.current = connection.id;
    setOauthFlowState('success');
    setStep(singleProvider.id === 'excel_online' ? 'excel_select' : 'config');
  }, [singleProvider, isLoadingExisting, oauthConnectedByProvider]);
  const providerMetaById = React.useMemo(
    () => new Map(availableProviders.map((provider) => [provider.id, provider])),
    [availableProviders]
  );
  const visibleExistingSources = React.useMemo(() =>
    existingSources.filter((source) => {
      if (!allowedProviderIds.has(source.provider)) return false;
      return isReusableSourceForFolder(source, folderId);
    }),
  [allowedProviderIds, existingSources, folderId]);
  const filteredExistingSources = React.useMemo(() => {
    const search = existingSearch.trim().toLowerCase();
    const visibleSources = visibleExistingSources;
    if (!search) return visibleSources;
    return visibleSources.filter((source) => source.name.toLowerCase().includes(search));
  }, [existingSearch, visibleExistingSources]);
  const getProviderIcon = (provider: DataSourceProvider) => {
    const providerMeta = providerMetaById.get(provider);
    return {
      iconPath: providerMeta?.iconPath,
      fallbackIcon: providerMeta?.fallbackIcon ?? '🗃️',
    };
  };
  const normalizeKey = (value: string) => value.toLowerCase().replace(/[\s_-]/g, '');
  const fieldLabel = (fieldId: string) =>
    fieldId
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (char) => char.toUpperCase())
      .trim();
  const mappingFields = React.useMemo(() => {
    if (!profile) return [];
    const required = new Set(profile.requiredProps ?? []);
    const recommended = new Set(profile.recommendedProps ?? []);
    const allFieldIds = Array.from(new Set(Object.keys(profile.fieldAliases ?? {})));
    const ordered = [
      ...allFieldIds.filter((id) => required.has(id)),
      ...allFieldIds.filter((id) => !required.has(id) && recommended.has(id)),
      ...allFieldIds.filter((id) => !required.has(id) && !recommended.has(id)),
    ];
    return ordered.map((id) => ({
      id,
      label: String(
        t(`orgchart.assistant.fields.${id}`, {
          defaultValue: fieldLabel(id),
        }),
      ),
    }));
  }, [profile, t]);
  const schemaHeaders = React.useMemo(
    () => schemaData?.schema.columns.map((column) => column.name) ?? [],
    [schemaData]
  );
  const requiredFieldIds = React.useMemo(
    () => mappingFields.filter((field) => (profile?.requiredProps ?? []).includes(field.id)).map((field) => field.id),
    [mappingFields, profile]
  );
  const mappedFields = React.useMemo(
    () => mappingFields.filter((field) => fieldMappingDraft[field.id]),
    [mappingFields, fieldMappingDraft]
  );
  const unmappedFields = React.useMemo(
    () => mappingFields.filter((field) => !fieldMappingDraft[field.id]),
    [mappingFields, fieldMappingDraft]
  );
  const hasMissingRequiredMappings = React.useMemo(
    () => requiredFieldIds.some((fieldId) => !fieldMappingDraft[fieldId]),
    [requiredFieldIds, fieldMappingDraft]
  );
  const UNMAPPED_VALUE = '__unmapped__';
  const testIdSafe = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '-');
  const mappingSourcePhrases = React.useMemo(() => {
    const fallbackPlural = t('assistant.connectDataSourceMapping.source.fallback.plural', {
      defaultValue: 'source fields',
    });
    const fallbackSingular = t('assistant.connectDataSourceMapping.source.fallback.singular', {
      defaultValue: 'field',
    });
    if (!selectedProvider) {
      return { plural: String(fallbackPlural), singular: String(fallbackSingular) };
    }
    return {
      plural: String(
        t(`assistant.connectDataSourceMapping.source.${selectedProvider}.plural`, {
          defaultValue: fallbackPlural,
        }),
      ),
      singular: String(
        t(`assistant.connectDataSourceMapping.source.${selectedProvider}.singular`, {
          defaultValue: fallbackSingular,
        }),
      ),
    };
  }, [selectedProvider, t]);

  useEffect(() => {
    if (!selectedExistingId) return;
    const stillVisible = filteredExistingSources.some((source) => source.id === selectedExistingId);
    if (!stillVisible) {
      setSelectedExistingId(null);
    }
  }, [filteredExistingSources, selectedExistingId]);

  // Handle selecting an existing data source
  const handleSelectExisting = (sourceId: string) => {
    setSelectedExistingId(sourceId);
  };

  const handleUseExisting = () => {
    if (selectedExistingId) {
      onComplete?.(selectedExistingId);
    }
  };

  const resetProviderFlow = (provider: DataSourceProvider) => {
    setSelectedProvider(provider);
    if (PER_USER_PROVIDER_OWNERSHIP.has(provider)) {
      setOwnership('personal');
    } else if (CENTRALIZED_PROVIDER_OWNERSHIP.has(provider)) {
      setOwnership('org');
    }
    const providerLabel = t(`assistant.dataSourceProvider.${provider}.name`, {
      defaultValue:
        PROVIDERS.find((p) => p.id === provider)?.name ??
        t('assistant.dataSourceDefaultProviderLabel', { defaultValue: 'Data Source' }),
    });
    setName(
      t('assistant.connectDataSourceDefaultName', {
        label: providerLabel,
        defaultValue: 'My {{label}}',
      }),
    );
    setConfig({});
    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = '';
    }
    setCreatedSourceId(null);
    setOauthFlowState('idle');
    setOauthFlowMessage(null);
    setSelectedDriveId('');
    setSelectedWorkbookId('');
    setSelectedWorksheetId('');
    setGoogleSheetsRequested(false);
    setSelectedSheetName('');
    setFieldMappingDraft({});
    setIsFieldMappingInitialized(false);
    setShowUnmappedFields(false);
    oauthSourceIdRef.current = null;
  };

  const handleProceedAfterConnection = React.useCallback(async (sourceId: string) => {
    setError(null);
    const applyPermissionGuard = (rawMessage: string | null | undefined): boolean => {
      if (!isRestrictedAdminProvider(selectedProvider)) return false;
      if (!isLikelyProviderPermissionError(rawMessage)) return false;
      const permissionMessage = translateOauthPermission(selectedProvider);
      setError(permissionMessage);
      setOauthFlowState('error');
      setOauthFlowMessage(permissionMessage);
      return true;
    };

    try {
      const result = await testConnection.mutateAsync(sourceId);
      if (result.success) {
        if (embedded && skipFieldMapping) {
          onComplete?.(sourceId);
        } else if (!skipFieldMapping && supportsFieldMappingStep(selectedProvider) && profile) {
          setStep('mapping');
        } else {
          setStep('schema');
        }
      } else {
        const rawError =
          result.error ?? t('assistant.dataSourceErrorConnectionTestFailed', { defaultValue: 'Connection test failed' });
        if (applyPermissionGuard(rawError)) return;
        setError(rawError);
        if (isLikelyOAuthCredentialError(rawError)) {
          setOauthFlowState('error');
          setOauthFlowMessage(rawError);
        }
      }
    } catch (err) {
      const rawError =
        err instanceof Error
          ? err.message
          : t('assistant.dataSourceErrorConnectionTestFailed', { defaultValue: 'Connection test failed' });
      if (applyPermissionGuard(rawError)) return;
      setError(rawError);
      if (isLikelyOAuthCredentialError(rawError)) {
        setOauthFlowState('error');
        setOauthFlowMessage(rawError);
      }
    }
  }, [
    embedded,
    onComplete,
    profile,
    selectedProvider,
    skipFieldMapping,
    supportsFieldMappingStep,
    t,
    testConnection,
    translateOauthPermission,
  ]);

  useEffect(() => {
    const handleOAuthCallback = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data as Partial<OAuthCallbackMessage> | null;
      if (!data || data.type !== 'oauth_callback') return;

      if (data.success) {
        setOauthFlowState('success');
        setOauthFlowMessage(
          t('assistant.oauthAuthorizationSuccessContinue', {
            defaultValue: 'Authorization successful. Continue setup.',
          }),
        );
        if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
          oauthWindowRef.current.close();
        }
        if (selectedProvider === 'excel_online') {
          setStep('excel_select');
        } else if (selectedProvider === 'google_sheets') {
          setStep('config');
        } else if (selectedProvider === 'azure_ad') {
          const connectedSourceId = oauthSourceIdRef.current ?? createdSourceId;
          if (connectedSourceId) {
            void handleProceedAfterConnection(connectedSourceId);
          }
        } else if (selectedProvider === 'google_workspace') {
          const connectedSourceId = oauthSourceIdRef.current ?? createdSourceId;
          if (connectedSourceId) {
            void handleProceedAfterConnection(connectedSourceId);
          }
        } else if (selectedProvider === 'bamboohr') {
          const connectedSourceId = oauthSourceIdRef.current ?? createdSourceId;
          if (connectedSourceId) {
            void handleProceedAfterConnection(connectedSourceId);
          }
        }
      } else {
        const rawError =
          data.error ??
          t('assistant.oauthAuthorizationFailed', { defaultValue: 'Authorization failed. Please try again.' });
        if (isRestrictedAdminProvider(selectedProvider) && isLikelyProviderPermissionError(rawError)) {
          const permissionMessage = translateOauthPermission(selectedProvider);
          setError(permissionMessage);
          setOauthFlowState('error');
          setOauthFlowMessage(permissionMessage);
        } else {
          setOauthFlowState('error');
          setOauthFlowMessage(rawError);
        }
      }

      oauthWindowRef.current = null;
    };

    window.addEventListener('message', handleOAuthCallback);
    return () => {
      window.removeEventListener('message', handleOAuthCallback);
    };
  }, [createdSourceId, handleProceedAfterConnection, selectedProvider, t, translateOauthPermission]);

  const ensureOAuthSource = async (
    provider: 'google_sheets' | 'google_workspace' | 'excel_online' | 'azure_ad'
  ): Promise<string> => {
    const connectedSource = oauthConnectedByProvider.get(provider);
    if (connectedSource?.id) {
      return connectedSource.id;
    }

    const providerLabel = t(`assistant.dataSourceProvider.${provider}.name`, {
      defaultValue:
        PROVIDERS.find((p) => p.id === provider)?.name ??
        t('assistant.dataSourceDefaultProviderLabel', { defaultValue: 'Data Source' }),
    });
    const sourceName = `${providerLabel} account`;

    const result = await create.mutateAsync({
      provider,
      name: sourceName,
      ownership,
      config: {
        _schemaVersion: 1,
      },
    });
    return result.dataSource.id;
  };

  const handleOAuthProviderConnect = async (
    provider: 'google_sheets' | 'google_workspace' | 'excel_online' | 'azure_ad'
  ) => {
    setError(null);
    try {
      resetProviderFlow(provider);
      const sourceId = await ensureOAuthSource(provider);
      setCreatedSourceId(sourceId);
      oauthSourceIdRef.current = sourceId;

      const existing = oauthConnectedByProvider.get(provider);
      if (existing?.connected) {
        setOauthFlowState('success');
        setOauthFlowMessage(
          t('assistant.oauthAlreadyConnected', { defaultValue: 'Already connected.' }),
        );
        if (provider === 'google_sheets') {
          setStep('config');
        } else if (provider === 'azure_ad') {
          await handleProceedAfterConnection(existing.id);
        } else if (provider === 'google_workspace') {
          await handleProceedAfterConnection(existing.id);
        } else {
          setStep('excel_select');
        }
        return;
      }

      handleAuthorizeProvider(provider, sourceId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('assistant.dataSourceErrorInitProvider', { defaultValue: 'Failed to initialize provider connection' }),
      );
    }
  };

  const handleAuthorizeProvider = (
    provider: 'google_sheets' | 'google_workspace' | 'excel_online' | 'azure_ad' | 'bamboohr',
    sourceId: string
  ) => {
    const href = `/api/oauth/${provider}/authorize?dataSourceId=${encodeURIComponent(sourceId)}`;
    oauthWindowRef.current = window.open(href, '_blank', 'width=640,height=800');
    setOauthFlowState('authorizing');
    setOauthFlowMessage(
      t('assistant.oauthWaitingAuthorization', {
        defaultValue: 'Waiting for authorization to complete...',
      }),
    );
  };

  const handleProviderSelect = (provider: DataSourceProvider) => {
    if (!allowedProviderIds.has(provider)) {
      return;
    }
    resetProviderFlow(provider);
    if (supportsOAuth(provider)) {
      const existing = oauthConnectedByProvider.get(provider);
      if (existing?.connected) {
        setCreatedSourceId(existing.id);
        oauthSourceIdRef.current = existing.id;
        setOauthFlowState('success');
        setOauthFlowMessage(
          t('assistant.oauthAlreadyConnected', { defaultValue: 'Already connected.' }),
        );
        if (provider === 'excel_online') {
          setStep('excel_select');
        } else if (provider === 'google_sheets') {
          setStep('config');
        } else if (provider === 'azure_ad') {
          void handleProceedAfterConnection(existing.id);
        } else {
          void handleProceedAfterConnection(existing.id);
        }
      }
      return;
    }
    if (provider === 'bamboohr') {
      setStep('bamboohr_config');
      return;
    }
    setStep('config');
  };

  const handleProviderCardClick = (
    provider: ProviderInfo
  ) => {
    if (!provider.available) return;

    if (supportsOAuth(provider.id)) {
      const oauthProviderId = provider.id as
        | 'google_sheets'
        | 'google_workspace'
        | 'excel_online'
        | 'azure_ad';
      const oauthStatus = oauthConnectedByProvider.get(provider.id);

      if (oauthStatus?.connected) {
        handleProviderSelect(provider.id);
        return;
      }

      void handleOAuthProviderConnect(oauthProviderId);
      return;
    }

    handleProviderSelect(provider.id);
  };

  const handleConfigSubmit = async () => {
    if (!selectedProvider || !name.trim() || supportsOAuth(selectedProvider)) return;

    setError(null);

    try {
      const result = await create.mutateAsync({
        provider: selectedProvider,
        name: name.trim(),
        ownership,
        config: {
          _schemaVersion: 1,
          ...config,
        },
      });

      setCreatedSourceId(result.dataSource.id);
      await handleProceedAfterConnection(result.dataSource.id);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('assistant.dataSourceErrorCreate', { defaultValue: 'Failed to create data source' }),
      );
    }
  };

  const handleBambooHrSubmit = async () => {
    if (selectedProvider !== 'bamboohr' || !name.trim()) return;
    if (!config.companyDomain?.trim()) return;

    setError(null);

    try {
      const result = await create.mutateAsync({
        provider: 'bamboohr',
        name: name.trim(),
        ownership,
        config: {
          _schemaVersion: 1,
          companyDomain: config.companyDomain.trim(),
        },
      });

      setCreatedSourceId(result.dataSource.id);
      oauthSourceIdRef.current = result.dataSource.id;
      handleAuthorizeProvider('bamboohr', result.dataSource.id);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('assistant.dataSourceErrorCreateBamboohr', { defaultValue: 'Failed to create BambooHR source' }),
      );
    }
  };

  const handleFetchGoogleSheets = useCallback(async () => {
    const spreadsheetRef = config.spreadsheetId?.trim() ?? '';
    if (!createdSourceId || !spreadsheetRef) return;
    if (!isValidGoogleSpreadsheetReference(spreadsheetRef)) return;

    setError(null);
    setGoogleSheetsRequested(true);
    setSelectedSheetName('');
    lastRequestedSpreadsheetRef.current = spreadsheetRef;
  }, [config.spreadsheetId, createdSourceId]);

  useEffect(() => {
    if (
      selectedProvider !== 'google_sheets' ||
      step !== 'config' ||
      oauthFlowState !== 'success' ||
      !createdSourceId
    ) {
      return;
    }

    const spreadsheetRef = config.spreadsheetId?.trim() ?? '';
    if (!isValidGoogleSpreadsheetReference(spreadsheetRef)) {
      setGoogleSheetsRequested(false);
      return;
    }

    if (lastRequestedSpreadsheetRef.current === spreadsheetRef) {
      return;
    }
    void handleFetchGoogleSheets();
  }, [
    selectedProvider,
    step,
    oauthFlowState,
    createdSourceId,
    config.spreadsheetId,
    handleFetchGoogleSheets,
  ]);

  const handleGoogleSelectionContinue = async () => {
    const credentialSourceId = oauthSourceIdRef.current ?? createdSourceId;
    if (
      !credentialSourceId
      || !folderId
      || !googleSheetsData?.spreadsheet
      || !selectedSheetName
    ) return;

    setError(null);
    try {
      const selectedSheet = googleSheetsData.sheets.find(
        (sheet) => sheet.name === selectedSheetName,
      );
      if (!selectedSheet) return;
      const endpoint = await create.mutateAsync({
        provider: 'google_sheets',
        name: `${googleSheetsData.spreadsheet.title} · ${selectedSheet.name}`,
        ownership: 'personal',
        scope: 'folder',
        folderId,
        credentialSourceId,
        config: {
          _schemaVersion: 1,
          spreadsheetId: googleSheetsData.spreadsheet.id,
          sheetName: selectedSheet.name,
          sheetId: selectedSheet.id,
        },
      });

      setCreatedSourceId(endpoint.dataSource.id);
      await handleProceedAfterConnection(endpoint.dataSource.id);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('assistant.dataSourceErrorUpdateGoogleSheets', {
              defaultValue: 'Failed to update Google Sheets source',
            }),
      );
    }
  };

  const handleWorkbookChange = (workbookId: string) => {
    setSelectedWorkbookId(workbookId);
    setSelectedWorksheetId('');
  };

  const handleDriveChange = (driveId: string) => {
    setSelectedDriveId(driveId);
    setSelectedWorkbookId('');
    setSelectedWorksheetId('');
  };

  const handleExcelSelectionContinue = async () => {
    const credentialSourceId = oauthSourceIdRef.current ?? createdSourceId;
    if (!credentialSourceId || !folderId || !selectedDriveId || !selectedWorkbookId || !selectedWorksheetId) return;

    setError(null);
    try {
      const workbook = excelWorkbooksData?.workbooks.find(
        (candidate) => candidate.id === selectedWorkbookId,
      );
      const worksheet = excelWorksheetsData?.worksheets.find(
        (candidate) => candidate.id === selectedWorksheetId,
      );
      if (!workbook || !worksheet) return;
      const drive = excelDrivesData?.drives.find((candidate) => candidate.id === selectedDriveId);
      if (!drive) return;
      const endpoint = await create.mutateAsync({
        provider: 'excel_online',
        name: `${workbook.name} · ${worksheet.name}`,
        ownership: 'personal',
        scope: 'folder',
        folderId,
        credentialSourceId,
        config: {
          _schemaVersion: 1,
          driveId: selectedDriveId,
          driveName: drive.name,
          workbookId: selectedWorkbookId,
          workbookName: workbook.name,
          worksheetId: selectedWorksheetId,
          worksheetName: worksheet.name,
        },
      });

      setCreatedSourceId(endpoint.dataSource.id);
      await handleProceedAfterConnection(endpoint.dataSource.id);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('assistant.dataSourceErrorUpdateExcel', { defaultValue: 'Failed to update Excel source' }),
      );
    }
  };

  const handleComplete = async () => {
    if (createdSourceId) {
      const fieldMappings =
        supportsFieldMappingStep(selectedProvider) && Object.keys(fieldMappingDraft).length > 0
          ? Object.entries(fieldMappingDraft)
              .filter(([, sourceColumn]) => typeof sourceColumn === 'string' && sourceColumn.length > 0)
              .map(([targetField, sourceColumn]) => ({
                sourceColumn: sourceColumn as string,
                targetField,
              }))
          : undefined;
      
      const keyColumn = fieldMappings?.find((mapping) => mapping.targetField === 'positionId')?.sourceColumn
        ?? fieldMappings?.find((mapping) => mapping.targetField === 'personId')?.sourceColumn;
      await update.mutateAsync({
        id: createdSourceId,
        config: { keyColumn },
      });
      onComplete?.(createdSourceId, fieldMappings);
    }
  };

  const initializeFieldMapping = React.useCallback(() => {
    if (!profile || mappingFields.length === 0 || schemaHeaders.length === 0) return;
    const next: Record<string, string | null> = {};
    const normalizedHeaders = schemaHeaders.map((header) => ({
      header,
      normalized: normalizeKey(header),
    }));
    for (const field of mappingFields) {
      const aliases = profile.fieldAliases?.[field.id] ?? [];
      const normalizedAliases = [field.id, ...aliases].map((alias) => normalizeKey(alias));
      const match = normalizedHeaders.find((header) => normalizedAliases.includes(header.normalized));
      next[field.id] = match?.header ?? null;
    }
    setFieldMappingDraft(next);
    setIsFieldMappingInitialized(true);
  }, [profile, mappingFields, schemaHeaders]);

  useEffect(() => {
    if (step !== 'mapping' || isFieldMappingInitialized || !supportsFieldMappingStep(selectedProvider)) return;
    initializeFieldMapping();
  }, [
    step,
    isFieldMappingInitialized,
    selectedProvider,
    initializeFieldMapping,
    supportsFieldMappingStep,
  ]);

  const chooseNewSource = () => {
    setError(null);
    if (selectedProvider && PER_USER_PROVIDER_OWNERSHIP.has(selectedProvider) && oauthFlowState === 'success') {
      setStep(selectedProvider === 'excel_online' ? 'excel_select' : 'config');
    } else if (singleProvider && oauthConnectedByProvider.get(singleProvider.id)?.connected) {
      handleProviderSelect(singleProvider.id);
    } else {
      setStep('provider');
    }
  };
  const busy = create.isPending || testConnection.isPending || oauthFlowState === 'authorizing';
  const selectionError = step === 'excel_select'
    ? workbooksError ?? worksheetsError
    : step === 'config' && selectedProvider === 'google_sheets' ? googleSheetsError : null;
  const displayedError = error ?? selectionError?.message;
  const selectedExcelResourceExists = Boolean(
    excelWorkbooksData?.workbooks.some((workbook) => workbook.id === selectedWorkbookId)
    && excelWorksheetsData?.worksheets.some((worksheet) => worksheet.id === selectedWorksheetId),
  );
  const selectedGoogleSheetExists = Boolean(
    googleSheetsData?.sheets.some((sheet) => sheet.name === selectedSheetName),
  );

  return (
    <div className={cn('w-full text-foreground', !embedded && 'bg-card font-inter text-card-foreground rounded-lg shadow-lg max-w-lg border border-border')} data-testid="tid.datasource.wizard">
      {/* Header */}
      {!embedded && <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <h2 className="text-lg font-medium text-[var(--assistant-title)]" data-testid="tid.datasource.wizard.title">
          {t('assistant.connectDataSourceDialogTitle', { defaultValue: 'Connect Data Source' })}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
          aria-label={String(
            t('assistant.connectDataSourceDialogCloseAria', { defaultValue: 'Close' }),
          )}
          data-testid="tid.datasource.wizard.close"
        >
          ✕
        </button>
      </div>}

      {/* Content */}
      <div className={embedded ? 'space-y-6' : 'p-6'}>

        {embedded && singleProvider && step !== 'choose' && visibleExistingSources.length > 0 && (
          <div className="flex justify-end">
            <Button variant="link" size="sm" className="h-auto p-0" disabled={busy} onClick={() => setStep('choose')}>
              {t('assistant.importUseExisting', { defaultValue: 'Use an existing source' })}
            </Button>
          </div>
        )}

        {displayedError ? (
          <div role="alert" className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="tid.datasource.wizard.error">
            <div>{displayedError}</div>
            {supportsOAuth(selectedProvider) && isLikelyOAuthCredentialError(displayedError) && oauthSourceIdRef.current ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => handleAuthorizeProvider(selectedProvider, oauthSourceIdRef.current!)}
                data-testid="tid.datasource.wizard.reconnect"
              >
                {t('assistant.reconnectDataSource', { defaultValue: 'Reconnect account' })}
              </Button>
            ) : null}
          </div>
        ) : null}

        {/* Step 0: Choose existing or create new */}
        {step === 'choose' && (
          <div>
            {isLoadingExisting ? (
              <div className="text-center py-4 text-gray-500">
                {t('assistant.loadingDataSources', { defaultValue: 'Loading data sources...' })}
              </div>
            ) : visibleExistingSources.length > 0 ? (
              <>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-slate-600 font-normal text-sm">
                    {t('assistant.selectExistingDataSource', {
                      defaultValue: 'Select an existing data source',
                    })}
                  </p>
                  <div className="relative w-full max-w-[162px]">
                    <input
                      type="text"
                      value={existingSearch}
                      onChange={(event) => setExistingSearch(event.target.value)}
                      placeholder={t('assistant.searchDataSourcePlaceholder', {
                        defaultValue: 'Search data source...',
                      })}
                      className="w-full h-7 rounded-md border border-input bg-background pr-9 pl-3 text-xs text-foreground font-normal placeholder:text-slate-300 dark:placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-ring/30"
                    />
                    <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  </div>
                </div>

                {/* Existing sources list */}
                <div className="mb-4">
                  <div className="border border-border rounded-md divide-y divide-border max-h-[147px] overflow-auto">
                    {filteredExistingSources.map((source) => {
                      const providerIcon = getProviderIcon(source.provider);
                      return (
                      <button
                        key={source.id}
                        type="button"
                        onClick={() => handleSelectExisting(source.id)}
                        className={`w-full px-3 py-2 text-left transition-colors flex items-center gap-3 ${
                          selectedExistingId === source.id
                            ? 'bg-sky-50 dark:bg-background ring-1 ring-sky-500 dark:ring-primary ring-inset'
                            : 'hover:bg-accent'
                        }`}
                      >
                        {providerIcon.iconPath ? (
                          <img
                            src={providerIcon.iconPath}
                            alt={String(
                              t('assistant.dataSourceProviderIconAlt', {
                                name: t(`assistant.dataSourceProvider.${source.provider}.name`, {
                                  defaultValue: source.provider,
                                }),
                                defaultValue: `${source.provider} icon`,
                              }),
                            )}
                            className="h-5 w-5 shrink-0"
                          />
                        ) : (
                          <span className="text-base leading-none" aria-hidden>
                            {providerIcon.fallbackIcon}
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-normal text-sm text-[var(--assistant-title)] truncate">{source.name}</div>
                        </div>
                        {selectedExistingId === source.id && (
                          <CircleCheckBig className="h-4 w-4 text-primary shrink-0" />
                        )}
                      </button>
                      );
                    })}
                    {filteredExistingSources.length === 0 && (
                      <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                        {t('assistant.noDataSourcesInSearch', {
                          defaultValue: 'No data sources found.',
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {!embedded && <div className="flex justify-between items-center pt-2">
                  <button
                    type="button"
                    onClick={() => setStep('provider')}
                    className="text-sm font-medium flex items-center gap-1 text-sky-700 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300" 
                    data-testid="tid.datasource.wizard.create-new"
                  >
                    <Plus className="h-4 w-4" />
                    {t('assistant.createDataSource', { defaultValue: 'Create Data Source' })}
                  </button>
                  <Button
                    type="button"
                    onClick={handleUseExisting}
                    disabled={!selectedExistingId}
                    className="px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="tid.datasource.wizard.use-selected"
                  >
                    {t('assistant.useSelectedDataSource', { defaultValue: 'Use Selected' })}
                  </Button>
                </div>}
              </>
            ) : (
              <div className="flex flex-col items-center border rounded-xl justify-center text-center py-6">
                <div className="rounded-2xl bg-sky-100 dark:bg-sky-900/40 p-4 mb-4">
                  <Upload className="h-8 w-8 text-primary" />
                </div>
                <p className="text-[var(--assistant-title)] font-medium text-[16px] mb-6">
                  {t('assistant.noExistingDataSource', {
                    defaultValue: 'No existing data source found.',
                  })}
                </p>
                <Button
                  type="button"
                  onClick={() => setStep('provider')}
                  className="min-w-[220px] h-[38px] rounded-md px-4 py-2"
                  data-testid="tid.datasource.wizard.create-first"
                >
                  {t('assistant.createDataSource', { defaultValue: 'Create Data Source' })}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Provider Selection */}
        {step === 'provider' && !singleProvider && (
          <div dir="auto" data-testid="tid.datasource.wizard.provider-step">
            <p className="text-sm mb-4 text-slate-600 dark:text-slate-300">
              {t('assistant.selectDataSourceType', {
                defaultValue: 'Select data source type to use',
              })}
            </p>
            <div className="grid gap-1.5">
              {availableProviders.map((provider) => {
                const copy = translateProvider(provider);
                const isOAuthProvider = supportsOAuth(provider.id);
                const oauthStatus = isOAuthProvider ? oauthConnectedByProvider.get(provider.id) : null;
                return (
                  <React.Fragment key={provider.id}>
                    <div
                      data-testid={`tid.datasource.provider.${provider.id}`}
                      role="button"
                      tabIndex={provider.available ? 0 : -1}
                      onClick={() => handleProviderCardClick(provider)}
                      onKeyDown={(event) => {
                        if (!provider.available) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleProviderCardClick(provider);
                        }
                      }}
                      className={`p-1.5 rounded-md text-left transition-colors border ${
                      provider.available
                        ? 'border-border bg-card hover:border-[#0C5E96] hover:bg-sky-50 dark:bg-slate-900 dark:hover:bg-slate-800 cursor-pointer'
                        : 'border-border bg-muted opacity-50 cursor-not-allowed'
                    }`}
                    >
                      <div className="flex items-center gap-3">
                        {provider.iconPath ? (
                        <img
                          src={provider.iconPath}
                          alt={String(
                            t('assistant.dataSourceProviderIconAlt', {
                              name: copy.name,
                              defaultValue: `${copy.name} icon`,
                            }),
                          )}
                          className="h-7 w-7 shrink-0"
                        />
                      ) : (
                        <span className="text-2xl leading-none">{provider.fallbackIcon}</span>
                      )}
                        <div className="flex-1">
                          <div className="font-normal text-sm text-[var(--assistant-title)]">
                            {copy.name}
                            {!provider.available && (
                              <span className="ml-2 text-sm text-muted-foreground">
                                {t('assistant.dataSourceProviderComingSoon', {
                                  defaultValue: '(Coming soon)',
                                })}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {copy.description}
                          </div>
                        </div>
                        {provider.available && isOAuthProvider && (
                          <div className="flex items-center gap-1.5">
                            {oauthStatus?.connected ? (
                              <div className="inline-flex items-center gap-1 rounded-full">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                <span className="text-xs font-medium text-[var(--assistant-title)]">
                                  {t('assistant.dataSourceOAuthConnected', { defaultValue: 'Connected' })}
                                </span>
                              </div>
                            ) : (
                              <span className="h-1.5 w-1.5 rounded-full bg-rose-300" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {provider.id === 'bamboohr' && isOrgChartProfile
                      ? COMING_SOON_HRIS_PROVIDERS.map((placeholder) => {
                          const placeholderCopy = translateProvider(placeholder);
                          return (
                            <div
                              key={placeholder.id}
                              data-testid={`tid.datasource.provider.${placeholder.id}`}
                              aria-disabled="true"
                              className="p-1.5 rounded-md text-left transition-colors border border-border bg-muted opacity-50 cursor-not-allowed"
                            >
                              <div className="flex items-center gap-3">
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-background text-[10px] font-semibold text-muted-foreground">
                                  {placeholder.fallbackIcon}
                                </span>
                                <div className="flex-1">
                                  <div className="font-normal text-sm text-[var(--assistant-title)]">
                                    {placeholderCopy.name}
                                    <span className="ml-2 text-sm text-muted-foreground">
                                      {t('assistant.dataSourceProviderComingSoon', {
                                        defaultValue: '(Coming soon)',
                                      })}
                                    </span>
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {placeholderCopy.description}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      : null}
                  </React.Fragment>
                );
              })}
            </div>
            {selectedProvider === 'google_workspace' && (
              <div
                className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                dir="auto"
                data-testid="tid.datasource.provider.google-workspace.notice"
              >
                {t('assistant.connectDataSourceGoogleWorkspaceAdminNotice', {
                  defaultValue:
                    'Google Workspace Directory API access requires an authorized Google Workspace admin account. If a non-admin user authorizes this request, user import will fail due to API permission limits.',
                })}
              </div>
            )}
            {selectedProvider === 'azure_ad' && (
              <div
                className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                dir="auto"
                data-testid="tid.datasource.provider.azure-ad.notice"
              >
                {t('assistant.connectDataSourceAzureAdAdminNotice', {
                  defaultValue:
                    'Microsoft Graph directory access requires tenant admin consent for this app. A tenant admin must authorize the connection first; without admin consent, user import will fail.',
                })}
              </div>
            )}
            {supportsOAuth(selectedProvider) && oauthFlowState !== 'idle' && (
              <div
                className={`mt-4 rounded border px-3 py-2 text-sm ${
                  oauthFlowState === 'success'
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : oauthFlowState === 'error'
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-blue-200 bg-blue-50 text-blue-700'
                }`}
                dir="auto"
                data-testid="tid.datasource.provider.oauth-status"
              >
                {oauthFlowMessage}
              </div>
            )}
            {visibleExistingSources.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => setStep('choose')}
                  className="text-sm font-medium flex items-center gap-1 text-sky-700 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
                  data-testid="tid.datasource.wizard.back-to-existing"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('assistant.backToExistingSources', { defaultValue: 'Back to existing sources' })}
                </button>
              </div>
            )}
          </div>
        )}

        {step === 'provider' && singleProvider && (
          <div className="space-y-3 py-4">
            <h3 className="text-base font-semibold">
              {t('assistant.importConnectAccount', { defaultValue: 'Connect your {{provider}} account', provider: singleProvider.name })}
            </h3>
            <p className="max-w-xl text-sm text-muted-foreground">
              {t('assistant.importConnectAccountHint', { defaultValue: 'Authorize access to choose the data you want to import. You’ll review the fields before adding anything to this folder.' })}
            </p>
            {oauthFlowMessage && <p role="status" className="text-sm text-muted-foreground">{oauthFlowMessage}</p>}
          </div>
        )}

        {/* Step 2: Configuration */}
        {step === 'config' && (
          <div className="space-y-5">
            {selectedProvider === 'csv' && (
              <>
                <div>
                  <label className="block text-xs font-normal text-slate-700 dark:text-slate-200 mb-1">
                    {t('assistant.connectDataSourceConfigNameLabel', { defaultValue: 'Name' })}
                  </label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={String(
                      t('assistant.connectDataSourceConfigNamePlaceholder', {
                        defaultValue: 'Enter data source name',
                      }),
                    )}
                    className="w-full h-9 !text-sm font-normal focus-visible:ring-[0.8px]"
                    data-testid="tid.datasource.config.name"
                  />
                </div>

            <div>
              <label className="block text-xs font-normal text-slate-700 dark:text-slate-200 mb-1">
                {t('assistant.connectDataSourceConfigOwnershipLabel', { defaultValue: 'Ownership' })}
              </label>
              <Select
                value={ownership}
                data-testid="tid.datasource.config.ownership"
                onValueChange={(value) => setOwnership(value as OwnershipType)}
              >
                <SelectTrigger size="default" className="w-full !h-9 min-h-9 py-1 !text-sm font-normal focus-visible:ring-[0.8px]">
                  <SelectValue
                    placeholder={String(
                      t('assistant.connectDataSourceConfigOwnershipSelectPlaceholder', {
                        defaultValue: 'Select ownership',
                      }),
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem className='!text-sm' value="org">
                    {t('assistant.connectDataSourceConfigOwnershipOrg', {
                      defaultValue: 'Organization (shared)',
                    })}
                  </SelectItem>
                  <SelectItem className='!text-sm' value="personal">
                    {t('assistant.connectDataSourceConfigOwnershipPersonal', {
                      defaultValue: 'Personal (only you)',
                    })}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-1 ml-1">
                {ownership === 'org'
                  ? t('assistant.connectDataSourceConfigOwnershipHintOrg', {
                      defaultValue:
                        'Anyone in your organization can use this data source',
                    })
                  : t('assistant.connectDataSourceConfigOwnershipHintPersonal', {
                      defaultValue: 'Only you can access this data source',
                    })}
              </p>
            </div>
              </>
            )}

            {selectedProvider === 'google_sheets' && (
              <div dir="auto" className="space-y-4">
                <div className={embedded ? 'text-sm text-muted-foreground' : 'rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700'}>
                  {t('assistant.connectDataSourceGoogleReadyMessage', {
                    defaultValue:
                      'Google connection is ready. Select spreadsheet and sheet.',
                  })}
                </div>
                <div>
                  <label className="block text-xs font-normal text-slate-700 dark:text-slate-200 mb-1">
                    {t('assistant.connectDataSourceConfigSpreadsheetLabel', {
                      defaultValue: 'Spreadsheet URL or ID',
                    })}
                  </label>
                  <Input
                    type="text"
                    value={config.spreadsheetId ?? ''}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setConfig({ ...config, spreadsheetId: nextValue });
                      if (nextValue.trim() !== lastRequestedSpreadsheetRef.current) {
                        setGoogleSheetsRequested(false);
                        setSelectedSheetName('');
                      }
                    }}
                    placeholder={String(
                      t('assistant.connectDataSourceConfigSpreadsheetPlaceholder', {
                        defaultValue: 'https://docs.google.com/spreadsheets/d/...',
                      }),
                    )}
                    className="w-full h-9 !text-sm font-normal focus-visible:ring-[0.8px]"
                data-testid="tid.datasource.config.google.spreadsheet"
                  />
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                  <label htmlFor="import-google-sheet" className="text-sm font-medium">
                    {t('assistant.connectDataSourceConfigSheetLabel', { defaultValue: 'Sheet' })}
                  </label>
                  <Button variant="ghost" size="sm" disabled={!shouldLoadGoogleSheets || isRefreshingGoogleSheets || busy} onClick={() => { void refreshGoogleSheets(); }} aria-label="Refresh sheets" data-testid="tid.datasource.google.refresh-sheets">
                    <RefreshCw className={cn('mr-1.5 size-3.5', isRefreshingGoogleSheets && 'animate-spin')} />
                    {t('assistant.importRefreshList', { defaultValue: 'Refresh list' })}
                  </Button>
                  </div>
                  <select
                    id="import-google-sheet"
                    value={selectedSheetName}
                    onChange={(e) => setSelectedSheetName(e.target.value)}
                    disabled={!googleSheetsRequested || isLoadingGoogleSheets}
                    className="w-full p-2 border rounded disabled:opacity-50"
                    data-testid="tid.datasource.config.google.sheet-name"
                  >
                    <option value="">
                      {!googleSheetsRequested
                        ? t('assistant.connectDataSourceConfigSheetOptionPlaceholderIdle', {
                            defaultValue: 'Add spreadsheet and fetch sheets first',
                          })
                        : isLoadingGoogleSheets
                          ? t('assistant.connectDataSourceConfigSheetOptionPlaceholderLoading', {
                              defaultValue: 'Loading sheets...',
                            })
                          : t('assistant.connectDataSourceConfigSheetOptionPlaceholderSelect', {
                              defaultValue: 'Select a sheet',
                            })}
                    </option>
                    {(googleSheetsData?.sheets ?? []).map((sheet) => (
                      <option key={sheet.id} value={sheet.name}>
                        {sheet.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {selectedProvider === 'csv' && (
              <div className="space-y-1.5" dir="auto">
                <label className="block text-xs font-normal text-slate-700 dark:text-slate-200 mb-1">
                  {t('assistant.connectDataSourceConfigCsvFileLabel', {
                    defaultValue: 'CSV file',
                  })}
                </label>
                <input
                  ref={csvFileInputRef}
                  type="file"
                  accept=".csv"
                  className="sr-only"
                  data-testid="tid.datasource.config.csv-file"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = () => {
                        setConfig({
                          ...config,
                          content: reader.result as string,
                          filename: file.name,
                        });
                      };
                      reader.readAsText(file);
                    }
                  }}
                />
                <div className="flex min-h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    onClick={() => csvFileInputRef.current?.click()}
                    data-testid="tid.datasource.config.csv-file-trigger"
                  >
                    {t('assistant.connectDataSourceCsvChooseFileButton', {
                      defaultValue: 'Choose file',
                    })}
                  </Button>
                  <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                    {config.filename ??
                      t('assistant.connectDataSourceCsvNoFileChosen', {
                        defaultValue: 'No file chosen',
                      })}
                  </span>
                </div>
              </div>
            )}

            {!embedded && <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={() => setStep('provider')}
                className="px-4 py-2 text-sm font-medium flex items-center gap-1 text-sky-700 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
                data-testid="tid.datasource.config.back"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('assistant.connectDataSourceWizardBack', { defaultValue: 'Back' })}
              </button>
              {selectedProvider === 'google_sheets' ? (
                <Button
                  type="button"
                  onClick={handleGoogleSelectionContinue}
                  disabled={!selectedSheetName || create.isPending || testConnection.isPending}
                  className="px-4 py-2 bg-primary rounded disabled:opacity-50"
                  data-testid="tid.datasource.config.continue"
                >
                  {create.isPending || testConnection.isPending
                    ? t('assistant.dataSourceConnecting', { defaultValue: 'Connecting...' })
                    : t('assistant.dataSourceContinue', { defaultValue: 'Continue' })}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleConfigSubmit}
                  disabled={!name.trim() || create.isPending}
                  className="px-4 py-2 bg-primary rounded disabled:opacity-50"
                  data-testid="tid.datasource.config.continue"
                >
                  {create.isPending
                    ? t('assistant.connectDataSourceWizardCreating', {
                        defaultValue: 'Creating...',
                      })
                    : t('assistant.dataSourceContinue', { defaultValue: 'Continue' })}
                </Button>
              )}
            </div>}
          </div>
        )}

        {/* Step 3 (BambooHR): Domain configuration + OAuth */}
        {step === 'bamboohr_config' && (
          <div className="space-y-5" dir="auto" data-testid="tid.datasource.bamboohr.config">
            <div>
              <label className="block text-xs font-normal text-slate-700 dark:text-slate-200 mb-1">
                {t('assistant.connectDataSourceConfigNameLabel', { defaultValue: 'Name' })}
              </label>
              <Input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={String(
                  t('assistant.connectDataSourceBambooHrNamePlaceholder', {
                    defaultValue: 'My BambooHR directory',
                  }),
                )}
                className="w-full h-9 !text-sm font-normal focus-visible:ring-[0.8px]"
                data-testid="tid.datasource.bamboohr.name"
              />
            </div>

            <div>
              <label className="block text-xs font-normal text-slate-700 dark:text-slate-200 mb-1">
                {t('assistant.connectDataSourceConfigOwnershipLabel', { defaultValue: 'Ownership' })}
              </label>
              <Input
                type="text"
                value={String(
                  t('assistant.connectDataSourceBambooHrOwnershipReadonly', {
                    defaultValue: 'Organization (shared)',
                  }),
                )}
                readOnly
                className="w-full h-9 !text-sm font-normal bg-muted/60"
                data-testid="tid.datasource.bamboohr.ownership"
              />
              <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-1 ml-1">
                {t('assistant.connectDataSourceBambooHrOwnershipHint', {
                  defaultValue:
                    'BambooHR connections are shared across your organization.',
                })}
              </p>
            </div>

            <div>
              <label className="block text-xs font-normal text-slate-700 dark:text-slate-200 mb-1">
                {t('assistant.connectDataSourceBambooHrCompanyDomainLabel', {
                  defaultValue: 'Company domain',
                })}
              </label>
              <Input
                type="text"
                value={config.companyDomain ?? ''}
                onChange={(event) => setConfig({ ...config, companyDomain: event.target.value })}
                placeholder={String(
                  t('assistant.connectDataSourceBambooHrCompanyDomainPlaceholder', {
                    defaultValue: 'your-company or your-company.bamboohr.com',
                  }),
                )}
                className="w-full h-9 !text-sm font-normal focus-visible:ring-[0.8px]"
                data-testid="tid.datasource.bamboohr.company-domain"
              />
            </div>

            <div
              className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              data-testid="tid.datasource.bamboohr.oauth.notice"
            >
              {t('assistant.connectDataSourceBambooHrOAuthNotice', {
                defaultValue:
                  'BambooHR import uses OAuth. An authorized BambooHR admin account must approve access before employee directory data can be imported.',
              })}
            </div>

            {!embedded && <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={() => setStep('provider')}
                className="px-4 py-2 text-sm font-medium flex items-center gap-1 text-sky-700 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
                data-testid="tid.datasource.bamboohr.back"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('assistant.connectDataSourceWizardBack', { defaultValue: 'Back' })}
              </button>
              <Button
                type="button"
                onClick={handleBambooHrSubmit}
                disabled={
                  !name.trim() ||
                  !config.companyDomain?.trim() ||
                  create.isPending ||
                  oauthFlowState === 'authorizing' ||
                  testConnection.isPending
                }
                className="px-4 py-2 bg-primary rounded disabled:opacity-50"
                data-testid="tid.datasource.bamboohr.continue"
              >
                {create.isPending || oauthFlowState === 'authorizing' || testConnection.isPending
                  ? t('assistant.dataSourceConnecting', { defaultValue: 'Connecting...' })
                  : t('assistant.dataSourceContinue', { defaultValue: 'Continue' })}
              </Button>
            </div>}
            {(oauthFlowState === 'authorizing' || oauthFlowState === 'error') && (
              <div
                className={`rounded border px-3 py-2 text-xs ${
                  oauthFlowState === 'error'
                    ? 'border-red-200 bg-red-50 text-red-700'
                    : 'border-blue-200 bg-blue-50 text-blue-700'
                }`}
                dir="auto"
                data-testid="tid.datasource.bamboohr.oauth-status"
              >
                {oauthFlowMessage ??
                  t('assistant.oauthWaitingAuthorization', {
                    defaultValue: 'Waiting for authorization to complete...',
                  })}
              </div>
            )}
          </div>
        )}

        {/* Step 4 (Excel): Workbook/Worksheet Selection */}
        {step === 'excel_select' && (
          <div className="space-y-4" dir="auto" data-testid="tid.datasource.excel.select">
            {!embedded && <div>
              <h3 className="font-medium mb-1">
                {t('assistant.connectDataSourceExcelTitle', { defaultValue: 'Choose Excel Source' })}
              </h3>
              <p className="text-sm text-gray-600">
                {t('assistant.connectDataSourceExcelIntro', {
                  defaultValue: 'Select the workbook and worksheet to import before field mapping.',
                })}
              </p>
            </div>}

            {embedded && <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <CircleCheckBig className="size-4 text-emerald-600" />
              {t('assistant.importMicrosoftConnected', { defaultValue: 'Microsoft account connected. Choose a workbook and worksheet.' })}
            </p>}

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="import-excel-drive" className="text-sm font-medium">OneDrive</label>
                <Button variant="ghost" size="sm" disabled={isRefreshingDrives || busy} onClick={() => { void refreshDrives(); }} aria-label="Refresh OneDrive drives" data-testid="tid.datasource.excel.refresh-drives">
                  <RefreshCw className={cn('mr-1.5 size-3.5', isRefreshingDrives && 'animate-spin')} />
                  {t('assistant.importRefreshList', { defaultValue: 'Refresh list' })}
                </Button>
              </div>
              <select
                id="import-excel-drive"
                value={selectedDriveId}
                onChange={(e) => handleDriveChange(e.target.value)}
                disabled={isLoadingDrives}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50"
                data-testid="tid.datasource.excel.drive"
              >
                <option value="">{isLoadingDrives ? 'Loading OneDrive drives...' : 'Select a OneDrive'}</option>
                {(excelDrivesData?.drives ?? []).map((drive) => (
                  <option key={drive.id} value={drive.id}>{drive.name}</option>
                ))}
              </select>
              {drivesError ? <p className="mt-1 text-xs text-red-600" data-testid="tid.datasource.excel.drives-error">{drivesError instanceof Error ? drivesError.message : 'Failed to load OneDrive drives'}</p> : null}
              {!isLoadingDrives && !drivesError && (excelDrivesData?.drives.length ?? 0) === 0 ? <p className="mt-1 text-xs text-muted-foreground" data-testid="tid.datasource.excel.drives-empty">No OneDrive drives available.</p> : null}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
              <label htmlFor="import-excel-workbook" className="text-sm font-medium">
                {t('assistant.connectDataSourceExcelWorkbookLabel', { defaultValue: 'Workbook' })}
              </label>
              <Button variant="ghost" size="sm" disabled={isRefreshingWorkbooks || busy} onClick={() => { void refreshWorkbooks(); }} aria-label="Refresh workbooks" data-testid="tid.datasource.excel.refresh-workbooks">
                <RefreshCw className={cn('mr-1.5 size-3.5', isRefreshingWorkbooks && 'animate-spin')} />
                {t('assistant.importRefreshList', { defaultValue: 'Refresh list' })}
              </Button>
              </div>
              <Combobox
                id="import-excel-workbook"
                data-testid="tid.datasource.excel.workbook"
                searchInputTestId="tid.datasource.excel.workbook-search"
                options={(excelWorkbooksData?.workbooks ?? []).map((workbook) => ({
                  value: workbook.id,
                  label: workbook.name,
                }))}
                value={selectedWorkbookId}
                onValueChange={handleWorkbookChange}
                placeholder={!selectedDriveId
                  ? 'Select a OneDrive first'
                  : isLoadingWorkbooks
                  ? t('assistant.connectDataSourceExcelWorkbookLoading', {
                      defaultValue: 'Loading workbooks...',
                    })
                  : t('assistant.connectDataSourceExcelWorkbookSelect', {
                      defaultValue: 'Select a workbook',
                    })}
                searchPlaceholder="Search workbooks..."
                emptyText="These are all the items we could find in your OneDrive. Please ensure that the file you want to bind is located in the root directory of your OneDrive."
                disabled={!selectedDriveId || isLoadingWorkbooks}
              />
              {workbooksError ? <p className="mt-1 text-xs text-red-600" data-testid="tid.datasource.excel.workbooks-error">{workbooksError instanceof Error ? workbooksError.message : 'Failed to load workbooks'}</p> : null}
              {!isLoadingWorkbooks && !workbooksError && selectedDriveId && (excelWorkbooksData?.workbooks.length ?? 0) === 0 ? <p className="mt-1 text-xs text-muted-foreground" data-testid="tid.datasource.excel.workbooks-empty">No Excel workbooks found in this OneDrive.</p> : null}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
              <label htmlFor="import-excel-worksheet" className="text-sm font-medium">
                {t('assistant.connectDataSourceExcelWorksheetLabel', { defaultValue: 'Worksheet' })}
              </label>
              <Button variant="ghost" size="sm" disabled={!selectedWorkbookId || isRefreshingWorksheets || busy} onClick={() => { void refreshWorksheets(); }} aria-label="Refresh worksheets" data-testid="tid.datasource.excel.refresh-worksheets">
                <RefreshCw className={cn('mr-1.5 size-3.5', isRefreshingWorksheets && 'animate-spin')} />
                {t('assistant.importRefreshList', { defaultValue: 'Refresh list' })}
              </Button>
              </div>
              <select
                id="import-excel-worksheet"
                value={selectedWorksheetId}
                onChange={(e) => setSelectedWorksheetId(e.target.value)}
                disabled={!selectedDriveId || !selectedWorkbookId || isLoadingWorksheets}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50"
                data-testid="tid.datasource.excel.worksheet"
              >
                <option value="">
                  {!selectedDriveId
                    ? 'Select a OneDrive first'
                    : !selectedWorkbookId
                    ? t('assistant.connectDataSourceExcelWorksheetSelectWorkbookFirst', {
                        defaultValue: 'Select workbook first',
                      })
                    : isLoadingWorksheets
                      ? t('assistant.connectDataSourceExcelWorksheetLoading', {
                          defaultValue: 'Loading worksheets...',
                        })
                      : t('assistant.connectDataSourceExcelWorksheetSelect', {
                          defaultValue: 'Select a worksheet',
                        })}
                </option>
                {(excelWorksheetsData?.worksheets ?? []).map((worksheet) => (
                  <option key={worksheet.id} value={worksheet.id}>
                    {worksheet.name}
                  </option>
                ))}
              </select>
            </div>

            {!embedded && <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={() => setStep('provider')}
                className="px-4 py-2 text-sm font-medium flex items-center gap-1 text-gray-600 hover:text-gray-800"
                data-testid="tid.datasource.excel.back"
              >
                <ArrowLeft className="h-4 w-4 shrink-0" />
                {t('assistant.connectDataSourceWizardBack', { defaultValue: 'Back' })}
              </button>
              <Button
                type="button"
                onClick={handleExcelSelectionContinue}
                disabled={!selectedDriveId || !selectedWorkbookId || !selectedWorksheetId || create.isPending || testConnection.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                data-testid="tid.datasource.excel.continue"
              >
                {create.isPending || testConnection.isPending
                  ? t('assistant.dataSourceConnecting', { defaultValue: 'Connecting...' })
                  : t('assistant.dataSourceContinue', { defaultValue: 'Continue' })}
              </Button>
            </div>}
          </div>
        )}
        {embedded && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
            <Button variant="ghost" disabled={busy} onClick={step === 'choose' ? chooseNewSource : onBack}>
              <ArrowLeft className="mr-1.5 size-4" />
              {t('assistant.connectDataSourceWizardBack', { defaultValue: 'Back' })}
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" disabled={busy} onClick={onCancel}>
                {t('assistant.importCancel', { defaultValue: 'Cancel' })}
              </Button>
              {step === 'choose' ? (
                <Button disabled={!selectedExistingId} onClick={handleUseExisting}>
                  {t('assistant.importContinueMapping', { defaultValue: 'Continue to field mapping' })}
                </Button>
              ) : step === 'provider' && singleProvider ? (
                <Button disabled={isLoadingExisting || busy} onClick={() => handleProviderCardClick(singleProvider)}>
                  {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                  {t('assistant.importConnect', { defaultValue: 'Connect {{provider}}', provider: singleProvider.name })}
                </Button>
              ) : (
                <Button
                  disabled={busy || Boolean(selectionError) || (step === 'excel_select'
                    ? !selectedExcelResourceExists || isRefreshingWorkbooks || isRefreshingWorksheets
                    : selectedProvider === 'google_sheets'
                      ? !selectedGoogleSheetExists || isRefreshingGoogleSheets
                      : !name.trim() || (step === 'bamboohr_config' && !config.companyDomain?.trim()))}
                  onClick={step === 'excel_select' ? handleExcelSelectionContinue
                    : step === 'bamboohr_config' ? handleBambooHrSubmit
                      : selectedProvider === 'google_sheets' ? handleGoogleSelectionContinue : handleConfigSubmit}
                >
                  {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                  {t('assistant.importContinueMapping', { defaultValue: 'Continue to field mapping' })}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Field Mapping */}
        {step === 'mapping' && (
          <div className="space-y-3" dir="auto" data-testid="tid.datasource.mapping">
            <div className="text-sm text-muted-foreground">
              {t('assistant.connectDataSourceMapping.intro', {
                sourcePlural: mappingSourcePhrases.plural,
                defaultValue: `We matched your ${mappingSourcePhrases.plural} to org chart fields. Verify the mapping below.`,
              })}
            </div>
            {isLoadingSchema && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('assistant.connectDataSourceMapping.loading', { defaultValue: 'Loading field mapping…' })}
              </div>
            )}
            <ScrollArea className="h-[280px] rounded-md border">
              <div className="divide-y">
                {mappedFields.map((field) => {
                  const isRequired = requiredFieldIds.includes(field.id);
                  return (
                    <div
                      key={field.id}
                      className="flex items-center gap-3 px-4 py-2.5"
                      data-testid={`tid.datasource.mapping.row.${field.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">
                          {field.label}
                          {isRequired && <span className="ml-1 text-destructive">*</span>}
                        </div>
                      </div>
                      <Select
                        value={fieldMappingDraft[field.id] ?? UNMAPPED_VALUE}
                        onValueChange={(value) => {
                          setFieldMappingDraft((prev) => ({
                            ...prev,
                            [field.id]: value === UNMAPPED_VALUE ? null : value,
                          }));
                        }}
                      >
                        <SelectTrigger className="w-[220px]" size="sm" data-testid={`tid.datasource.mapping.select.${field.id}`}>
                          <SelectValue
                            placeholder={String(
                              t('assistant.connectDataSourceMapping.selectColumnPlaceholder', {
                                defaultValue: 'Select column',
                              }),
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNMAPPED_VALUE} data-testid={`tid.datasource.mapping.option.${field.id}.${UNMAPPED_VALUE}`}>
                            <span className="text-muted-foreground">
                              {t('assistant.connectDataSourceMapping.noneOption', { defaultValue: '— None —' })}
                            </span>
                          </SelectItem>
                          {schemaHeaders.map((header) => (
                            <SelectItem
                              key={`${field.id}-${header}`}
                              value={header}
                              data-testid={`tid.datasource.mapping.option.${field.id}.${testIdSafe(header)}`}
                            >
                              {header}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}

                {unmappedFields.length > 0 && (
                  <>
                    <div className="bg-muted/40">
                      <button
                        type="button"
                        onClick={() => setShowUnmappedFields((prev) => !prev)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-muted/60 transition-colors"
                        data-testid="tid.datasource.mapping.toggle-unmapped"
                      >
                        <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', showUnmappedFields && 'rotate-90')} />
                        <span className="text-xs font-medium text-muted-foreground">
                          {t('assistant.connectDataSourceMapping.unmappedToggle', {
                            count: unmappedFields.length,
                            defaultValue:
                              unmappedFields.length === 1
                                ? '{{count}} field not found'
                                : '{{count}} fields not found',
                          })}
                        </span>
                      </button>
                    </div>
                    {showUnmappedFields && unmappedFields.map((field) => {
                      const isRequired = requiredFieldIds.includes(field.id);
                      return (
                        <div
                          key={field.id}
                          className="flex items-center gap-3 px-4 py-2.5 bg-muted/20"
                          data-testid={`tid.datasource.mapping.row.${field.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-muted-foreground">
                              {field.label}
                              {isRequired && <span className="ml-1 text-destructive">*</span>}
                            </div>
                          </div>
                          <Select
                            value={fieldMappingDraft[field.id] ?? UNMAPPED_VALUE}
                            onValueChange={(value) => {
                              setFieldMappingDraft((prev) => ({
                                ...prev,
                                [field.id]: value === UNMAPPED_VALUE ? null : value,
                              }));
                            }}
                          >
                            <SelectTrigger className="w-[220px]" size="sm" data-testid={`tid.datasource.mapping.select.${field.id}`}>
                              <SelectValue
                                placeholder={String(
                                  t('assistant.connectDataSourceMapping.selectColumnPlaceholder', {
                                    defaultValue: 'Select column',
                                  }),
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={UNMAPPED_VALUE} data-testid={`tid.datasource.mapping.option.${field.id}.${UNMAPPED_VALUE}`}>
                                <span className="text-muted-foreground">
                                  {t('assistant.connectDataSourceMapping.noneOption', { defaultValue: '— None —' })}
                                </span>
                              </SelectItem>
                              {schemaHeaders.map((header) => (
                                <SelectItem
                                  key={`${field.id}-${header}`}
                                  value={header}
                                  data-testid={`tid.datasource.mapping.option.${field.id}.${testIdSafe(header)}`}
                                >
                                  {header}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </ScrollArea>
            <div className="flex justify-between pt-3">
              <Button variant="outline" type="button" onClick={() => setStep('provider')} data-testid="tid.datasource.mapping.back">
                <span className="inline-flex items-center gap-1">
                  <ArrowLeft className="h-4 w-4" />
                  {t('assistant.connectDataSourceWizardBack', { defaultValue: 'Back' })}
                </span>
              </Button>
              <Button type="button" onClick={handleComplete} disabled={hasMissingRequiredMappings} data-testid="tid.datasource.mapping.save">
                {t('assistant.connectDataSourceMapping.save', { defaultValue: 'Save mapping' })}
              </Button>
            </div>
          </div>
        )}

        {/* Step 6: Schema Review */}
        {step === 'schema' && (
          <div dir="auto" data-testid="tid.datasource.schema">
            <div className="text-center mb-4">
              <div className="text-4xl mb-2">✓</div>
              <p className="text-green-600 font-medium">
                {t('assistant.connectDataSourceSchemaSuccess', {
                  defaultValue: 'Connected successfully!',
                })}
              </p>
            </div>

            {schemaData?.schema && (
              <div className="mb-4">
                <h3 className="font-medium mb-2">
                  {t('assistant.connectDataSourceSchemaDiscoveredColumns', {
                    defaultValue: 'Discovered Columns:',
                  })}
                </h3>
                <div className="border rounded divide-y max-h-48 overflow-auto">
                  {schemaData.schema.columns.map((col) => (
                    <div
                      key={col.name}
                      className="px-3 py-2 flex justify-between text-sm gap-2"
                    >
                      <span className="min-w-0 truncate">
                        {t(`orgchart.assistant.fields.${col.name}`, {
                          defaultValue: col.name,
                        })}
                      </span>
                      <span className="text-gray-500 shrink-0">
                        {t(
                          `assistant.connectDataSourceSchemaColumnType.${String(col.type).toLowerCase()}`,
                          { defaultValue: col.type },
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {schemaData?.suggestedKeyColumn && (
              <p className="text-sm text-gray-600 mb-4">
                {t('assistant.connectDataSourceSchemaSuggestedKey', {
                  defaultValue: 'Suggested key column:',
                })}{' '}
                <strong>
                  {t(`orgchart.assistant.fields.${schemaData.suggestedKeyColumn}`, {
                    defaultValue: schemaData.suggestedKeyColumn,
                  })}
                </strong>
              </p>
            )}

            <button
              type="button"
              onClick={handleComplete}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              data-testid="tid.datasource.schema.done"
            >
              {t('assistant.connectDataSourceSchemaDone', { defaultValue: 'Done' })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
